/**
 * How a run is displayed — shared by the run page, the job page and the
 * schedule page, so a status reads the same colour everywhere.
 */
import type { BackgroundJobRun, RunGroup, RunStatus } from '../../types/background-job.types';

export const RUN_STATUS_SEVERITY: Record<RunStatus, 'success' | 'danger' | 'warning' | 'info' | 'secondary'> = {
  SUCCESS: 'success',
  FAILED: 'danger',
  // not terminal — another attempt follows, so it reads as in progress
  RETRYING: 'warning',
  RUNNING: 'info',
  CANCELLED: 'secondary',
};

/** Still going: worth polling, and the only states a stop request can act on. */
export const isLiveStatus = (status?: RunStatus | null): boolean =>
  status === 'RUNNING' || status === 'RETRYING';

export const runDuration = (r: { startedAt?: string | null; endedAt?: string | null }): string => {
  if (!r.startedAt || !r.endedAt) return '—';
  const ms = Date.parse(r.endedAt) - Date.parse(r.startedAt);
  if (Number.isNaN(ms) || ms < 0) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
};

/**
 * Attempts folded into fires.
 *
 * A job with maxRetries: 2 that fails end-to-end writes THREE run documents
 * sharing one runCorrelationId. Listed flat, one incident reads as three.
 */
export function groupRuns(
  runs: BackgroundJobRun[],
  nameOf?: (jobId: string) => string | undefined,
): RunGroup[] {
  const byFire = new Map<string, BackgroundJobRun[]>();
  runs.forEach((r) => {
    const key = r.runCorrelationId || r._id;
    byFire.set(key, [...(byFire.get(key) ?? []), r]);
  });
  return [...byFire.entries()].map(([runCorrelationId, attempts]) => {
    const ordered = [...attempts].sort((a, b) => a.attemptNumber - b.attemptNumber);
    const last = ordered[ordered.length - 1];
    return {
      runCorrelationId,
      jobId: last.jobId,
      jobName: nameOf?.(last.jobId),
      triggeredBy: last.triggeredBy,
      // a fire's status is its highest attempt's
      status: last.status,
      attempts: ordered,
      startedAt: ordered[0].startedAt,
      endedAt: last.endedAt,
    };
  }).sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
}

/** The attempt a fire's page opens on — the latest, which carries the final status. */
export const latestAttempt = (g: RunGroup): BackgroundJobRun => g.attempts[g.attempts.length - 1];
